import React, { useEffect, useRef, useState } from 'react';
import { Lantern, StarSparkle, AID } from './AidOrnaments';

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function FloatingLantern({ x, delay = 0, scale = 1, dur = 6, swayDeg = 4 }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion()) return;
    const anim = el.animate(
      [
        { transform: `translateY(0px) rotate(-${swayDeg}deg)` },
        { transform: `translateY(-12px) rotate(${swayDeg}deg)` },
        { transform: `translateY(0px) rotate(-${swayDeg}deg)` },
      ],
      { duration: dur * 1000, iterations: Infinity, easing: 'ease-in-out', delay: delay * 1000 }
    );
    return () => anim.cancel();
  }, [dur, delay, swayDeg]);

  return (
    <div style={{
      position: 'absolute', top: 0, left: `${x}%`,
      transformOrigin: 'top center', pointerEvents: 'none',
    }}>
      <div ref={ref} style={{ transformOrigin: 'top center' }}>
        <Lantern size={70 * scale} color={AID.gold} glowColor={AID.goldLight} />
      </div>
    </div>
  );
}

export function GoldParticles({ count = 24 }) {
  const containerRef = useRef(null);
  const [particles] = useState(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left:    Math.random() * 100,
      size:    6 + Math.random() * 10,
      dur:     8 + Math.random() * 10,
      delay:   Math.random() * 12,
      drift:   -20 + Math.random() * 40,
      rot:     Math.random() * 720,
      opacity: 0.4 + Math.random() * 0.5,
    }))
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el || reducedMotion()) return;
    const anims = particles.map((p, i) => {
      const dot = el.children[i];
      if (!dot) return null;
      return dot.animate(
        [
          { transform: 'translate(0,-20px) rotate(0deg)', opacity: 0 },
          { transform: `translate(${p.drift / 2}px, 40vh) rotate(${p.rot / 2}deg)`, opacity: p.opacity, offset: 0.2 },
          { transform: `translate(${p.drift}px, 95vh) rotate(${p.rot}deg)`, opacity: 0 },
        ],
        { duration: p.dur * 1000, iterations: Infinity, delay: p.delay * 1000, easing: 'linear' }
      );
    });
    return () => anims.forEach(a => a?.cancel());
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {particles.map(p => (
        <div key={p.id} style={{ position: 'absolute', top: 0, left: `${p.left}%`, opacity: 0 }}>
          <div className="aid-particle">
            <StarSparkle size={p.size} color={AID.goldLight} opacity={1} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function TwinkleStars({ count = 14 }) {
  const containerRef = useRef(null);
  const [stars] = useState(() =>
    Array.from({ length: count }, (_, i) => ({
      id:    i,
      top:   Math.random() * 90,
      left:  Math.random() * 100,
      size:  5 + Math.random() * 8,
      dur:   2 + Math.random() * 3,
      delay: Math.random() * 3,
    }))
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el || reducedMotion()) return;
    const anims = stars.map((s, i) => {
      const dot = el.children[i];
      if (!dot) return null;
      return dot.animate(
        [
          { opacity: 0.15, transform: 'scale(0.8)' },
          { opacity: 0.85, transform: 'scale(1.1)' },
          { opacity: 0.15, transform: 'scale(0.8)' },
        ],
        { duration: s.dur * 1000, iterations: Infinity, delay: s.delay * 1000, easing: 'ease-in-out' }
      );
    });
    return () => anims.forEach(a => a?.cancel());
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {stars.map(s => (
        <div key={s.id} style={{ position: 'absolute', top: `${s.top}%`, left: `${s.left}%` }}>
          <StarSparkle size={s.size} color={AID.goldLight} />
        </div>
      ))}
    </div>
  );
}

export function useShimmerAnim(ref, dismissed = false) {
  useEffect(() => {
    const el = ref?.current;
    if (!el || dismissed || reducedMotion()) return;
    const anim = el.animate(
      [{ backgroundPosition: '0% 50%' }, { backgroundPosition: '200% 50%' }],
      { duration: 5000, iterations: Infinity, easing: 'linear' }
    );
    return () => anim.cancel();
  }, [ref, dismissed]);
}

export function useSwayAnim(ref, dur = 3500, disabled = false) {
  const animRef = useRef(null);
  useEffect(() => {
    const el = ref?.current;
    if (!el || disabled || reducedMotion()) return;
    const anim = el.animate(
      [
        { transform: 'translateY(0px) rotate(-4deg)' },
        { transform: 'translateY(-10px) rotate(4deg)' },
        { transform: 'translateY(0px) rotate(-4deg)' },
      ],
      { duration: dur, iterations: Infinity, easing: 'ease-in-out' }
    );
    animRef.current = anim;
    return () => { anim.cancel(); animRef.current = null; };
  }, [ref, dur, disabled]);
  return animRef;
}
